import { useCallback, useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { WalletCard } from "../components/WalletCard";
import { WalletFundingSources } from "../components/WalletFundingSources";
import { WalletCardLoad } from "../components/WalletCardLoad";
import { Html5Qrcode } from "html5-qrcode";
import { startAuthentication, startRegistration } from "@simplewebauthn/browser";

const API = "/api"; // same-origin — the ingress/worker proxies /api to the backend

const fmtUsd = (n) =>
  n == null
    ? "—"
    : `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* ---------- iPhone install prompt ---------- */
import { IOSInstallBanner } from "@/components/IOSInstallBanner";

export default function Wallet() {
  const [token, setToken] = useState(() => localStorage.getItem("wallet_token") || "");
  const [me, setMe] = useState(null);
  const [loginForm, setLoginForm] = useState(() => {
    try {
      const u = new URLSearchParams(window.location.search).get("uuid");
      return { ledger_uuid: u || "", phone: "" };
    } catch {
      return { ledger_uuid: "", phone: "" };
    }
  });
  const [loginError, setLoginError] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);

  const [mode, setMode] = useState("card"); // card | receive | pay | confirm | receipt
  const [recvForm, setRecvForm] = useState({ amount: "", memo: "" });
  const [payRequest, setPayRequest] = useState(null); // {code, payload}
  const [copied, setCopied] = useState(false);
  const [payCode, setPayCode] = useState("");
  const [scanned, setScanned] = useState(null); // fetched pay-request details
  const [payAmount, setPayAmount] = useState("");
  const [payError, setPayError] = useState("");
  // set when a Face ID approval fires but the device holds no passkey for this site
  // (key lives on another phone/browser, or was never enrolled here) — recovery, not a dead end
  const [passkeyRecovery, setPasskeyRecovery] = useState(false);

  const handlePayError = (err) => {
    if (err.name === "NotAllowedError" && me?.passkey_enrolled) {
      setPasskeyRecovery(true);
      setPayError("No Face ID key for this site on this phone — payment not sent.");
    } else {
      setPasskeyRecovery(false);
      setPayError(err.name === "NotAllowedError" ? "Face ID approval cancelled — payment not sent." : err.message);
    }
  };
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);

  // custody desk (physical cash in/out)
  const [custody, setCustody] = useState(null); // null=unknown, false=inactive, object=active
  const [custodyPoints, setCustodyPoints] = useState([]);
  const [cashOut, setCashOut] = useState({ open: false, point_id: "", amount: "" });
  const [custodyBusy, setCustodyBusy] = useState(false);
  const [custodyMsg, setCustodyMsg] = useState("");

  // merchant bank wire-out (queues now; settles when the NexorONE outgoing rail lands)
  const [wireOut, setWireOut] = useState({ open: false, amount: "", bank_label: "" });
  const [wireOuts, setWireOuts] = useState([]);
  const [wireOutBusy, setWireOutBusy] = useState(false);
  const [wireOutMsg, setWireOutMsg] = useState("");

  // remote funding rails: retail wire/ACH desk + my custody cash window (if I hold a point)
  const [wireInfo, setWireInfo] = useState(null);
  const [intentions, setIntentions] = useState([]);
  const [fundOpen, setFundOpen] = useState(false);
  const [fundAmount, setFundAmount] = useState("");
  const [fundBusy, setFundBusy] = useState(false);
  const [fundMsg, setFundMsg] = useState("");
  const [fundRail, setFundRail] = useState("ZELLE");
  const [desk, setDesk] = useState(null); // custody points I hold
  const [deskForm, setDeskForm] = useState({ member_uuid: "", amount: "", note: "", voucher_id: "" });
  const [deskFx, setDeskFx] = useState({ currency: "", amount: "", rate: "" });
  const [deskBusy, setDeskBusy] = useState(false);
  const [deskMsg, setDeskMsg] = useState("");

  // biometric passkey (Face ID / Touch ID payment approval)
  const [passkeyMsg, setPasskeyMsg] = useState("");
  const [passkeyBusy, setPasskeyBusy] = useState(false);

  const enrollPasskey = async () => {
    setPasskeyBusy(true);
    setPasskeyMsg("");
    try {
      const optRes = await fetch(`${API}/wallet/passkey/register/options`, {
        method: "POST", headers: authHeaders,
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      const options = await optRes.json();
      if (!optRes.ok) throw new Error(typeof options.detail === "string" ? options.detail : "Could not start enrollment");
      const credential = await startRegistration({ optionsJSON: options });
      const verRes = await fetch(`${API}/wallet/passkey/register/verify`, {
        method: "POST", headers: authHeaders,
        body: JSON.stringify({ credential, origin_url: window.location.origin }),
      });
      const verData = await verRes.json();
      if (!verRes.ok) throw new Error(typeof verData.detail === "string" ? verData.detail : "Enrollment failed");
      setPasskeyMsg("Face ID approval is ON — every payment now requires your scan.");
      loadMe();
    } catch (err) {
      setPasskeyMsg(err.name === "NotAllowedError" ? "Enrollment cancelled." : err.message);
    } finally {
      setPasskeyBusy(false);
    }
  };

  const biometricApprove = async (binding) => {
    const optRes = await fetch(`${API}/wallet/passkey/pay-options`, {
      method: "POST", headers: authHeaders,
      body: JSON.stringify({ ...binding, origin_url: window.location.origin }),
    });
    const data = await optRes.json();
    if (!optRes.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Could not start approval");
    const credential = await startAuthentication({ optionsJSON: data.options });
    return { binding_id: data.binding_id, credential, origin_url: window.location.origin };
  };

  const authHeaders = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  // member dues — settle straight into the director's treasury
  const TREASURY_ADDRESS = "0x889e5Cc83d2f972ED6fD5a1E58b6C5A7EFB83040";
  const [dues, setDues] = useState({ amount: "", busy: false, msg: "" });

  // BMEX stored-value card (in-loop, balance-only; register pulls by scanning its QR)
  const [card, setCard] = useState(null);
  const [cardBusy, setCardBusy] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [claimMsg, setClaimMsg] = useState("");
  const claimReward = async (kind) => {
    setClaiming(true);
    setClaimMsg("");
    try {
      const res = await fetch(`${API}/wallet/redeem-reward`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ kind }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Claim failed");
      setClaimMsg(`Claimed — ${data.label}. Show at the register to collect.`);
      loadCard();
    } catch (err) {
      setClaimMsg(err.message);
    } finally {
      setClaiming(false);
    }
  };
  const loadCard = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/wallet/card`, { headers: authHeaders });
      if (res.ok) setCard(await res.json());
    } catch { /* card is additive — never block the wallet */ }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Metal Desk — live spot for the four card metals + melt value of each tier's card stock
  const [metals, setMetals] = useState(null);
  // Ledger room — member's fractional property titles + safe keeping receipts
  const [holdings, setHoldings] = useState(null);
  // family covenant — the shared household tape (every /MAINTAIN settle, with block numbers)
  const [covenantTape, setCovenantTape] = useState(null);
  const loadHoldings = useCallback(async () => {
    if (!token) return;
    try {
      const [tr, sr, cr, er, ct] = await Promise.all([
        fetch(`${API}/wallet/deed-titles`, { headers: authHeaders }),
        fetch(`${API}/wallet/my-skrs`, { headers: authHeaders }),
        fetch(`${API}/wallet/my-tcu`, { headers: authHeaders }),
        fetch(`${API}/wallet/asset-equity`, { headers: authHeaders }),
        fetch(`${API}/wallet/covenant-tape`, { headers: authHeaders }),
      ]);
      const titles = tr.ok ? (await tr.json()).titles : [];
      const skrs = sr.ok ? (await sr.json()).skrs : [];
      const tcu = cr.ok ? (await cr.json()).units : 0;
      const equity = er.ok ? await er.json() : { equity_usd: null, properties: [] };
      if (ct.ok) {
        const tape = await ct.json();
        if (tape.covenant) setCovenantTape(tape);
      }
      if (titles.length || skrs.length || tcu > 0 || (equity.properties || []).length) setHoldings({ titles, skrs, tcu, equity });
    } catch { /* holdings are additive — never block the wallet */ }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  const loadMetals = useCallback(async () => {
    try {
      const res = await fetch(`${API}/metals/spot`);
      if (res.ok) setMetals(await res.json());
    } catch { /* metals board is additive — never block the wallet */ }
  }, []);

  // Away-game tape — an approved merchant watches today's register box score from the wallet
  const [tape, setTape] = useState(null);
  const loadTape = useCallback(async () => {
    if (!token || !me?.merchant_approved) return;
    try {
      const res = await fetch(`${API}/register/sales`, { headers: authHeaders });
      if (res.ok) setTape(await res.json());
    } catch { /* tape is additive — never block the wallet */ }
  }, [token, me?.merchant_approved]); // eslint-disable-line react-hooks/exhaustive-deps
  const rotateCard = async () => {
    if (!window.confirm("Replace this card? The current QR stops working immediately.")) return;
    setCardBusy(true);
    try {
      const res = await fetch(`${API}/wallet/card/rotate`, { method: "POST", headers: authHeaders });
      if (res.ok) setCard(await res.json());
    } finally {
      setCardBusy(false);
    }
  };

  // merchant card-charge / card-load (register pulls from, or loads onto, a member's card)
  const [charge, setCharge] = useState({ token: "", amount: "", memo: "", busy: false, result: null, error: "" });
  const [chargeMode, setChargeMode] = useState("charge"); // charge | load
  const [chargeScanning, setChargeScanning] = useState(false);
  const chargeScannerRef = useRef(null);
  const stopChargeScanner = async () => {
    if (chargeScannerRef.current) {
      try { await chargeScannerRef.current.stop(); chargeScannerRef.current.clear(); } catch { /* stopped */ }
      chargeScannerRef.current = null;
    }
    setChargeScanning(false);
  };
  const startChargeScanner = async () => {
    setCharge((c) => ({ ...c, error: "", result: null }));
    setChargeScanning(true);
    await new Promise((r) => setTimeout(r, 100));
    try {
      const scanner = new Html5Qrcode("charge-qr-reader");
      chargeScannerRef.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 220, height: 220 } },
        async (decoded) => {
          await stopChargeScanner();
          setCharge((c) => ({ ...c, token: String(decoded).replace(/^bmex:card:/i, "") }));
        },
        () => {}
      );
    } catch {
      setChargeScanning(false);
      setCharge((c) => ({ ...c, error: "Camera unavailable — paste the card token below." }));
    }
  };
  const submitCharge = async (e) => {
    e.preventDefault();
    setCharge((c) => ({ ...c, busy: true, error: "", result: null }));
    try {
      const res = await fetch(`${API}/register/card-${chargeMode}`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          card_token: charge.token,
          amount_usd: parseFloat(charge.amount),
          memo: charge.memo || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : data.detail?.message || "Charge failed");
      setCharge({ token: "", amount: "", memo: "", busy: false, result: data, error: "" });
      loadMe();
      loadCard();
    } catch (err) {
      setCharge((c) => ({ ...c, busy: false, error: err.message }));
    }
  };
  const payDues = async (e) => {
    e.preventDefault();
    setDues((d) => ({ ...d, busy: true, msg: "" }));
    try {
      const amount = parseFloat(dues.amount);
      const body = {
        to_address: TREASURY_ADDRESS,
        amount_usd: amount,
        memo: `Member dues — ${me?.label} — ${new Date().toLocaleString("en-US", { month: "long", year: "numeric" })}`,
      };
      if (me?.passkey_enrolled) {
        body.passkey = await biometricApprove({ to_address: TREASURY_ADDRESS, amount_usd: amount });
      }
      const res = await fetch(`${API}/wallet/pay`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : data.detail?.message || "Dues payment failed");
      setDues({ amount: "", busy: false, msg: `Dues paid — ${fmtUsd(amount)} settled to the director's treasury.` });
      loadMe();
    } catch (err) {
      setDues((d) => ({ ...d, busy: false, msg: err.name === "NotAllowedError" ? "Approval cancelled — nothing sent." : err.message }));
    }
  };

  const loadCustody = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/wallet/custody`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 403) {
        setCustody(false);
        return;
      }
      if (res.ok) {
        setCustody(await res.json());
        const pts = await fetch(`${API}/wallet/custody/points`, { headers: { Authorization: `Bearer ${token}` } });
        if (pts.ok) setCustodyPoints((await pts.json()).points || []);
        const dsk = await fetch(`${API}/wallet/custody/desk`, { headers: { Authorization: `Bearer ${token}` } });
        if (dsk.ok) setDesk(await dsk.json());
      }
    } catch {
      /* custody section non-blocking */
    }
  }, [token]);

  const loadFunding = useCallback(async () => {
    if (!token) return;
    try {
      const wi = await fetch(`${API}/wallet/wire-instructions`, { headers: { Authorization: `Bearer ${token}` } });
      if (wi.ok) setWireInfo(await wi.json());
      const fi = await fetch(`${API}/wallet/funding`, { headers: { Authorization: `Bearer ${token}` } });
      if (fi.ok) setIntentions((await fi.json()).intentions || []);
    } catch {
      /* funding section non-blocking */
    }
  }, [token]);

  const declareWire = async (e) => {
    e.preventDefault();
    setFundBusy(true);
    setFundMsg("");
    try {
      const res = await fetch(`${API}/wallet/funding-intention`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ amount_usd: parseFloat(fundAmount), rail: fundRail }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Could not declare the transfer");
      setFundMsg(`Declared — reference ${data.intention_id}. The desk settles your \$sUSD the moment your ${fundRail === "ZELLE" ? "Zelle" : "wire"} lands.`);
      setFundAmount("");
      loadFunding();
    } catch (err) {
      setFundMsg(err.message);
    } finally {
      setFundBusy(false);
    }
  };

  const deskAttest = async (e) => {
    e.preventDefault();
    setDeskBusy(true);
    setDeskMsg("");
    try {
      const res = await fetch(`${API}/custody/deposit`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          member_ledger_uuid: deskForm.member_uuid.trim(),
          custody_point_id: desk.held_points[0].point_id,
          amount_usd: parseFloat(deskForm.amount) || undefined,
          note: deskForm.note || undefined,
          voucher_id: deskForm.voucher_id || undefined,
          local_currency: deskFx.currency || undefined,
          local_amount: parseFloat(deskFx.amount) || undefined,
          fx_rate_to_usd: parseFloat(deskFx.rate) || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : data.detail?.message || "Could not log the deposit");
      setDeskMsg(`Logged — receipt ${data.receipt_id}. The depositor countersigns from their wallet to settle.`);
      setDeskForm({ member_uuid: "", amount: "", note: "", voucher_id: "" });
      setDeskFx({ currency: "", amount: "", rate: "" });
      loadCustody();
    } catch (err) {
      setDeskMsg(err.message);
    } finally {
      setDeskBusy(false);
    }
  };

  // pay-by-phone (the Apple Cash push twin): resolve -> confirm -> settle, Face ID if enrolled
  const [phonePay, setPhonePay] = useState({ phone: "", amount: "", resolved: null });
  const [phoneBusy, setPhoneBusy] = useState(false);
  const [deepLinkNote, setDeepLinkNote] = useState(""); // set when a /wallet?action=… link brought the member here

  const resolvePhone = async (overridePhone, overrideAmount) => {
    const phone = (overridePhone ?? phonePay.phone).trim();
    const amount = overrideAmount ?? phonePay.amount;
    setPhoneBusy(true);
    setPayError("");
    try {
      // a raw wallet address resolves right here — no directory lookup
      if (/^0x[0-9a-fA-F]{40}$/.test(phone)) {
        setPhonePay({ phone, amount, resolved: {
          label: `${phone.slice(0, 8)}…${phone.slice(-6)}`,
          address: phone, phone_masked: "wallet address", direct: true,
        }});
        return;
      }
      const res = await fetch(`${API}/wallet/resolve-phone`, {
        method: "POST", headers: authHeaders, body: JSON.stringify({ phone }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : data.detail?.message || "Lookup failed");
      setPhonePay({ phone, amount, resolved: data });
    } catch (err) {
      setPayError(err.message);
    } finally {
      setPhoneBusy(false);
    }
  };

  const sendPhonePay = async () => {
    setPhoneBusy(true);
    setPayError("");
    try {
      let passkey = null;
      if (me?.passkey_enrolled) {
        passkey = await biometricApprove({
          to_address: phonePay.resolved.address,
          amount_usd: parseFloat(phonePay.amount),
        });
      }
      // direct wallet-address pay vs phone-directory pay
      const res = phonePay.resolved?.direct
        ? await fetch(`${API}/wallet/pay`, {
            method: "POST", headers: authHeaders,
            body: JSON.stringify({
              to_address: phonePay.resolved.address,
              amount_usd: parseFloat(phonePay.amount),
              memo: "Direct wallet payment",
              passkey,
            }),
          })
        : await fetch(`${API}/wallet/pay-phone`, {
            method: "POST", headers: authHeaders,
            body: JSON.stringify({
              phone: phonePay.phone,
              amount_usd: parseFloat(phonePay.amount),
              passkey,
            }),
          });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : data.detail?.message || "Payment failed");
      setReceipt({
        status: "PAID",
        amount_usd: parseFloat(phonePay.amount),
        settlement: data.settlement_tx,
        to_label: phonePay.resolved?.direct ? phonePay.resolved.label : data.recipient?.label,
        note: phonePay.resolved?.direct ? `Sent to wallet ${phonePay.resolved.label}` : `Sent by phone to ${data.recipient?.label}`,
      });
      setPhonePay({ phone: "", amount: "", resolved: null });
      setDeepLinkNote("");
      setPasskeyRecovery(false);
      setMode("receipt");
      loadMe();
    } catch (err) {
      handlePayError(err);
    } finally {
      setPhoneBusy(false);
    }
  };

  // global cash-in voucher (BMEX_GLOBAL_CASHIN_V1) — scannable deposit intent for any window worldwide
  const [voucher, setVoucher] = useState(null);
  const [voucherBusy, setVoucherBusy] = useState(false);

  const showVoucher = async () => {
    setVoucherBusy(true);
    try {
      const res = await fetch(`${API}/wallet/cashin-voucher`, { method: "POST", headers: authHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error("Could not issue a voucher");
      setVoucher(data);
    } catch {
      /* leave voucher unset */
    } finally {
      setVoucherBusy(false);
    }
  };

  // director wire attestation (BMEX_NOSTRO_SETTLEMENT_V1) — VAULT window holders only
  const [wireAttest, setWireAttest] = useState({ open: false, target: "", amount: "", bank: "", ref: "" });
  const [attestBusy, setAttestBusy] = useState(false);
  const [attestMsg, setAttestMsg] = useState("");

  const submitWireAttest = async (e) => {
    e.preventDefault();
    setAttestBusy(true);
    setAttestMsg("");
    try {
      const vault = (desk?.held_points || []).find((p) => p.point_type === "VAULT");
      const amount = parseFloat(wireAttest.amount);
      const payload = {
        protocol: "BMEX_NOSTRO_SETTLEMENT_V1",
        meta: { timestamp: Math.floor(Date.now() / 1000), environment: "production", chain_id: 9940 },
        treasury_authority: {
          director_name: me?.label,
          phone_identifier: me?.phone,
          custody_point_id: vault.point_id,
        },
        wire_manifest: {
          origin_bank: wireAttest.bank,
          origin_account_masked: "······",
          fiat_currency: "USD",
          fiat_amount: amount,
          bank_reference_id: wireAttest.ref,
          settlement_status: "ATTESTED_RESERVE_CONFIRMED",
        },
        ledger_action: {
          target_member_wallet: wireAttest.target.trim(),
          mint_asset_contract: me?.susd_contract,
          token_amount_wei: (BigInt(Math.round(amount * 1e6)) * 10n ** 12n).toString(),
          director_authorization_signature: null,
        },
      };
      let passkey = null;
      if (me?.passkey_enrolled) {
        const optRes = await fetch(`${API}/v1/nostro/attestation-options`, {
          method: "POST", headers: authHeaders,
          body: JSON.stringify({ payload, origin_url: window.location.origin }),
        });
        const optData = await optRes.json();
        if (!optRes.ok) throw new Error(typeof optData.detail === "string" ? optData.detail : optData.detail?.message || "Could not start Face ID signing");
        const credential = await startAuthentication({ optionsJSON: optData.options });
        passkey = { binding_id: optData.binding_id, credential, origin_url: window.location.origin };
      }
      const res = await fetch(`${API}/v1/nostro/settlement`, {
        method: "POST", headers: authHeaders,
        body: JSON.stringify({ payload, passkey, origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : data.detail?.message || "Attestation failed");
      setAttestMsg(
        `Settled — ${fmtUsd(data.amount_usd)} minted to ${data.target_member_label} ` +
        `(block ${data.settlement_tx?.block}) · receipt ${data.receipt_id} · ${data.signature_state?.startsWith("VERIFIED") ? "Face ID signed" : "session signed"}`
      );
      setWireAttest({ open: true, target: "", amount: "", bank: "", ref: "" });
      loadMe();
    } catch (err) {
      setAttestMsg(err.name === "NotAllowedError" ? "Face ID signing cancelled — nothing settled." : err.message);
    } finally {
      setAttestBusy(false);
    }
  };

  const loadMe = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/wallet/me`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) {
        localStorage.removeItem("wallet_token");
        setToken("");
        setMe(null);
        return;
      }
      if (res.ok) setMe(await res.json());
    } catch {
      /* transient */
    }
  }, [token]);

  // Deep link: a QR scanned by the phone's native camera lands here with ?pay=CODE;
  // Face ID deep links land with ?action=pay|receive&to=&amount=&memo= — the wallet
  // opens pre-filled and the member's tap on send IS the biometric gesture (WebAuthn
  // requires a real user gesture, so the link cannot fire Face ID by itself).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const action = params.get("action");
    if (action && token) {
      const to = params.get("to") || "";
      const amount = params.get("amount") || "";
      const memo = params.get("memo") || "";
      if (action === "pay" && to) {
        setDeepLinkNote("Payment link loaded — review the recipient and approve with Face ID.");
        setMode("pay");
        setScanned(null);
        setPayCode("");
        setPayError("");
        resolvePhone(to, amount);
      } else if (action === "receive") {
        setDeepLinkNote("Receive link loaded — your QR is ready below.");
        setRecvForm({ amount, memo });
        setPayRequest(null);
        setPayError("");
        setMode("receive");
      } else {
        return;
      }
      window.history.replaceState({}, "", "/wallet");
      return;
    }
    const code = params.get("pay");
    if (code && token) {
      setMode("pay");
      fetchRequest(code);
      window.history.replaceState({}, "", "/wallet");
      return;
    }
    if (params.get("uuid")) {
      // guest-onboard deep link: UUID was pre-filled into the login form at mount
      window.history.replaceState({}, "", "/wallet");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    loadMe();
    loadCustody();
    loadFunding();
    loadCard();
    loadMetals();
    loadTape();
    loadHoldings();
    const t = setInterval(() => {
      loadMe();
      loadCustody();
      loadFunding();
      loadMetals();
      loadTape();
      loadHoldings();
    }, 20000);
    return () => clearInterval(t);
  }, [loadMe, loadCustody, loadFunding, loadCard, loadMetals, loadTape, loadHoldings]);

  useEffect(() => () => { stopChargeScanner(); }, []);

  const stopScanner = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        scannerRef.current.clear();
      } catch {
        /* already stopped */
      }
      scannerRef.current = null;
    }
    setScanning(false);
  };

  useEffect(() => () => { stopScanner(); }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginBusy(true);
    setLoginError("");
    try {
      const res = await fetch(`${API}/wallet/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(loginForm),
      });
      const data = await res.json();
      if (!res.ok) {
        // detail can be a string, a structured object, or FastAPI's 422 array — always render words
        const d = data?.detail;
        const msg = typeof d === "string" ? d
          : Array.isArray(d) ? d.map((x) => x?.msg || JSON.stringify(x)).join("; ")
          : (d?.message || d?.code) || "Sign-in failed — check the connection and try again";
        throw new Error(msg);
      }
      localStorage.setItem("wallet_token", data.token);
      setToken(data.token);
    } catch (err) {
      const offline = err.name === "TypeError" || err.name === "SyntaxError";
      setLoginError(offline ? "Couldn't reach the exchange — it may be mid-restart; retry in a few seconds." : err.message);
    } finally {
      setLoginBusy(false);
    }
  };

  const createRequest = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { memo: recvForm.memo || null };
      if (recvForm.amount) body.amount_usd = parseFloat(recvForm.amount);
      const res = await fetch(`${API}/wallet/pay-request`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Could not create code");
      setPayRequest(data);
    } catch (err) {
      setPayError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const fetchRequest = async (code) => {
    // Accepts: raw code, legacy "bmex:pay:CODE", or a full URL "<origin>/wallet?pay=CODE"
    // (the register QR payload) — so the in-app scanner handles every code format we mint.
    let clean = String(code).trim();
    const urlMatch = clean.match(/[?&]pay=([A-Za-z0-9]+)/i);
    if (urlMatch) clean = urlMatch[1];
    clean = clean.replace(/^bmex:pay:/i, "").toUpperCase();
    if (!clean) return;
    setPayError("");
    try {
      const res = await fetch(`${API}/wallet/pay-request/${clean}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Code not found");
      setScanned(data);
      setPayAmount(data.amount_usd != null ? String(data.amount_usd) : "");
      setMode("confirm");
    } catch (err) {
      setPayError(err.message);
    }
  };

  const startScanner = async () => {
    setPayError("");
    setScanning(true);
    await new Promise((r) => setTimeout(r, 100));
    try {
      const scanner = new Html5Qrcode("wallet-qr-reader");
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 220, height: 220 } },
        async (decoded) => {
          await stopScanner();
          fetchRequest(decoded);
        },
        () => {}
      );
    } catch {
      setScanning(false);
      setPayError("Camera unavailable — use the manual code entry below.");
    }
  };

  const executePay = async () => {
    setBusy(true);
    setPayError("");
    try {
      const body = { request_code: scanned.code };
      if (scanned.amount_usd == null) body.amount_usd = parseFloat(payAmount);
      if (me?.passkey_enrolled) {
        body.passkey = await biometricApprove({
          request_code: scanned.code,
          amount_usd: scanned.amount_usd == null ? parseFloat(payAmount) : undefined,
        });
      }
      const res = await fetch(`${API}/wallet/pay`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : data.detail?.message || "Payment failed");
      setReceipt(data);
      setScanned(null);
      setPasskeyRecovery(false);
      setMode("receipt");
      loadMe();
    } catch (err) {
      handlePayError(err);
    } finally {
      setBusy(false);
    }
  };

  const confirmDeposit = async (receiptId) => {
    setCustodyBusy(true);
    setCustodyMsg("");
    try {
      const res = await fetch(`${API}/custody/deposit/${receiptId}/confirm`, {
        method: "POST",
        headers: authHeaders,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Confirm failed");
      setCustodyMsg(`Deposit settled — ${fmtUsd(data.settlement?.amount_usd)} added to your wallet`);
      loadMe();
      loadCustody();
    } catch (err) {
      setCustodyMsg(err.message);
    } finally {
      setCustodyBusy(false);
    }
  };

  const submitCashOut = async (e) => {
    e.preventDefault();
    setCustodyBusy(true);
    setCustodyMsg("");
    try {
      const res = await fetch(`${API}/custody/redeem`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          custody_point_id: cashOut.point_id,
          amount_usd: parseFloat(cashOut.amount),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Redemption failed");
      setCustodyMsg(`Cash-out receipt ${data.receipt_id} — show this code at ${data.custody_point_label} to collect ${fmtUsd(data.amount_usd)}`);
      setCashOut({ open: false, point_id: "", amount: "" });
      loadMe();
      loadCustody();
    } catch (err) {
      setCustodyMsg(err.message);
    } finally {
      setCustodyBusy(false);
    }
  };

  const loadWireOuts = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/wallet/wireout-requests`, { headers: authHeaders });
      if (res.ok) setWireOuts((await res.json()).requests || []);
    } catch { /* rail preview — list failure is non-fatal */ }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (me?.merchant_approved) loadWireOuts();
  }, [me, loadWireOuts]);

  const submitWireOut = async (e) => {
    e.preventDefault();
    setWireOutBusy(true);
    setWireOutMsg("");
    try {
      const res = await fetch(`${API}/wallet/wireout-request`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          amount_usd: parseFloat(wireOut.amount),
          bank_label: wireOut.bank_label,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Could not queue wire-out");
      setWireOutMsg(`Wire-out ${data.request_id} queued — ${fmtUsd(data.amount_usd)} to ${data.bank_label}. Settles when the bank rail opens.`);
      setWireOut({ open: false, amount: "", bank_label: "" });
      loadWireOuts();
    } catch (err) {
      setWireOutMsg(err.message);
    } finally {
      setWireOutBusy(false);
    }
  };

  /* ---------- direct bank rail (NexorONE cash-out — live when whitelisted) ---------- */
  const [cashRail, setCashRail] = useState({ live: false, cashouts: [] });
  const [cashOutForm, setCashOutForm] = useState({ open: false, amount: "" });
  const [cashOutBusy, setCashOutBusy] = useState(false);
  const [cashOutMsg, setCashOutMsg] = useState("");

  const loadCashRail = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/wallet/cashouts`, { headers: authHeaders });
      if (res.ok) {
        const data = await res.json();
        setCashRail({ live: Boolean(data.rail_live), cashouts: data.cashouts || [] });
      }
    } catch { /* rail status is non-fatal */ }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (token) loadCashRail();
  }, [token, loadCashRail]);

  const submitRailCashOut = async (e) => {
    e.preventDefault();
    setCashOutBusy(true);
    setCashOutMsg("");
    try {
      const res = await fetch(`${API}/wallet/cashout`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ amount_usd: parseFloat(cashOutForm.amount) }),
      });
      const data = await res.json();
      if (!res.ok) {
        const d = data.detail;
        throw new Error((d && d.message) || (typeof d === "string" ? d : "Cash-out failed"));
      }
      setCashOutMsg(`Cash-out ${data.payout_id} — ${fmtUsd(data.amount_usd)} settled at block #${data.block}; the bank rail is paying out.`);
      setCashOutForm({ open: false, amount: "" });
      loadCashRail();
      loadMe();
      loadCard();
    } catch (err) {
      setCashOutMsg(err.message);
    } finally {
      setCashOutBusy(false);
    }
  };

  /* ---------- signed-out view ---------- */
  if (!token) {
    return (
      <div data-testid="wallet-login" className="min-h-screen bg-stone-950 flex items-center justify-center px-6">
        <div className="w-full max-w-sm space-y-4">
        <IOSInstallBanner />
        <form
          onSubmit={handleLogin}
          className="w-full max-w-sm rounded-2xl border border-stone-800 bg-stone-900/60 p-8 space-y-5 backdrop-blur"
        >
          <div className="space-y-1">
            <span className="mono-tag text-[10px] uppercase tracking-widest text-amber-500">
              Bourg Merchant Exchange
            </span>
            <h1 className="text-2xl font-bold text-stone-100 display-serif">Member Wallet</h1>
            <p className="text-xs text-stone-400">
              Your cash, on your phone. Sign in with your ledger UUID and registered phone.
            </p>
          </div>
          <div className="space-y-3">
            <input
              data-testid="wallet-uuid-input"
              type="text"
              required
              placeholder="Ledger UUID (e.g. 1a2b3c4d-…)"
              value={loginForm.ledger_uuid}
              onChange={(e) => setLoginForm({ ...loginForm, ledger_uuid: e.target.value })}
              className="w-full rounded-lg border border-stone-700 bg-stone-950 px-4 py-3 text-sm text-stone-100 placeholder:text-stone-600 focus:border-amber-500 focus:outline-none"
            />
            <input
              data-testid="wallet-phone-input"
              type="tel"
              required
              placeholder="Registered phone (+1 …)"
              value={loginForm.phone}
              onChange={(e) => setLoginForm({ ...loginForm, phone: e.target.value })}
              className="w-full rounded-lg border border-stone-700 bg-stone-950 px-4 py-3 text-sm text-stone-100 placeholder:text-stone-600 focus:border-amber-500 focus:outline-none"
            />
          </div>
          {loginError && (
            <p data-testid="wallet-login-error" className="text-xs text-red-400">{loginError}</p>
          )}
          <button
            data-testid="wallet-login-button"
            type="submit"
            disabled={loginBusy}
            className="w-full rounded-lg bg-amber-500 py-3 text-sm font-bold uppercase tracking-widest text-stone-950 hover:bg-amber-400 transition-colors disabled:opacity-50"
          >
            {loginBusy ? "Unlocking…" : "Unlock Wallet"}
          </button>
          <a
            data-testid="appstore-install-link"
            href="https://apps.apple.com/app/id6753765418"
            target="_blank"
            rel="noopener noreferrer"
            className="flex w-full items-center justify-center gap-2 pt-1 text-xs font-semibold text-stone-400 hover:text-amber-400 transition-colors"
          >
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-current" aria-hidden="true">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08ZM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25Z"/>
            </svg>
            Prefer native? Get the iOS app
          </a>
        </form>
        </div>
      </div>
    );
  }

  /* ---------- signed-in shell ---------- */
  return (
    <div data-testid="wallet-app" className="min-h-screen bg-stone-950 px-4 py-8">
      <div className="mx-auto w-full max-w-md space-y-6">
        <IOSInstallBanner />
        <div className="flex items-center justify-between">
          <span className="mono-tag text-[10px] uppercase tracking-widest text-amber-500">
            BMEX Member Wallet
          </span>
          <button
            data-testid="wallet-logout-button"
            onClick={() => {
              localStorage.removeItem("wallet_token");
              setToken("");
              setMe(null);
            }}
            className="text-[10px] uppercase tracking-widest text-stone-500 hover:text-stone-300"
          >
            Sign out
          </button>
        </div>

        {/* cash card */}
        <div
          data-testid="wallet-cash-card"
          className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-stone-900 via-stone-950 to-stone-900 p-6 space-y-4 shadow-[0_0_40px_rgba(191,155,72,0.08)]"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs text-stone-400">{me?.label || "…"}</span>
            <span
              data-testid="wallet-chain-state"
              className={`text-[10px] uppercase tracking-widest ${
                me?.chain_state === "LIVE" ? "text-emerald-400" : "text-stone-500"
              }`}
            >
              {me?.chain_state === "LIVE" ? "● live · chain 9940" : "syncing…"}
            </span>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500">Cash balance</div>
            <div data-testid="wallet-balance" className="text-4xl font-extrabold text-stone-100 display-serif">
              {fmtUsd(me?.susd_balance)}
            </div>
            <div className="text-[10px] text-stone-500 mt-1">
              $sUSD · 100% asset-backed ·{" "}
              {me?.receipt
                ? `${Number(me.receipt.fractional_units_nominal).toLocaleString()} units receipted (${me.receipt.asset_class_code})`
                : "custody receipt pending"}
            </div>
            {me?.tier === "GUEST_TRANSACTIONAL" && (
              <p data-testid="guest-tier-notice" className="text-[10px] text-stone-500 leading-relaxed mt-1">
                Guest transactional wallet — for purchases at BMEX merchants and cash-out at custody
                points. Not a membership in The BOURGANIZATION Institute.
              </p>
            )}
          </div>
          <div className="text-[10px] text-stone-600 break-all font-mono">{me?.address}</div>
        </div>

        {/* actions */}
        {mode === "card" && (
          <>
            <WalletCard card={card} onRotate={rotateCard} rotating={cardBusy} onClaim={claimReward} claiming={claiming} claimMsg={claimMsg} />
            {metals && (
              <div data-testid="metal-desk" className="rounded-xl border border-stone-700 bg-stone-900/60 p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-amber-500/80">Metal Desk · live spot</p>
                  <p className="text-[9px] font-mono text-stone-500">{metals.provider}</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {metals.metals.map((m) => (
                    <div key={m.symbol} data-testid={`metal-${m.symbol.toLowerCase()}`} className="rounded-lg border border-stone-800 bg-stone-950/60 p-3">
                      <p className="text-xs font-bold text-stone-100">{m.metal} <span className="text-[9px] font-mono text-stone-500">{m.symbol}</span></p>
                      {m.oracle && (
                        <span data-testid={`metal-${m.symbol.toLowerCase()}-oracle-badge`} className="inline-block mt-1 px-1.5 py-0.5 rounded text-[8px] font-mono uppercase tracking-wider border border-emerald-500/50 text-emerald-400 bg-emerald-500/10">
                          {m.oracle === "ZURICH_METALS" ? "Zurich desk" : m.oracle === "LONDON_OTC" ? "London desk" : m.oracle}{m.purity_standard ? ` · ${m.purity_standard}` : ""}
                        </span>
                      )}
                      <p className="text-[10px] text-stone-400 mt-0.5">${m.usd_per_troy_ounce.toLocaleString("en-US", { maximumFractionDigits: 2 })}/ozt</p>
                      <p className="text-[10px] font-mono text-amber-400/90 mt-1">card melt ≈ ${m.card_melt_value_usd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                      <p className="text-[9px] text-stone-500">{m.card_tier}</p>
                    </div>
                  ))}
                </div>
                <p className="text-[9px] text-stone-500 mt-2 leading-snug">
                  Melt value is the market value of the metal in the card stock itself ({metals.card_stock}) — your stored balance and rewards ride on top of the metal.
                </p>
              </div>
            )}
            {holdings && (
              <div data-testid="ledger-holdings" className="rounded-xl border border-stone-700 bg-stone-900/60 p-4">
                <p className="text-[10px] font-mono uppercase tracking-widest text-amber-500/80 mb-2">Ledger Room — titles & custody</p>
                {holdings.tcu > 0 && (
                  <div data-testid="holding-tcu" className="flex justify-between font-mono text-[11px] py-1 border-b border-stone-800/60">
                    <span className="text-stone-200">BGNZN Trust certificate units</span>
                    <span className="text-amber-400">{holdings.tcu} TCU · {holdings.tcu}% of the estate</span>
                  </div>
                )}
                {(holdings.equity?.properties || []).map((p) => (
                  <div key={p.property_id} data-testid="holding-property" className="flex justify-between font-mono text-[11px] py-1 border-b border-stone-800/60">
                    <span className="text-stone-200">{p.property_class.replace(/_/g, " ")} <span className="text-stone-500">· {p.legal_metadata.slice(0, 32)}</span></span>
                    <span className="text-amber-400">titled · ${p.valuation_usd.toLocaleString("en-US", { maximumFractionDigits: 2 })}</span>
                  </div>
                ))}
                {holdings.equity?.equity_usd > 0 && (
                  <p data-testid="holding-equity-total" className="text-[9px] font-mono text-amber-500/80 mt-1">Attributed equity backing: ${holdings.equity.equity_usd.toLocaleString("en-US", { maximumFractionDigits: 2 })} — custody-locked, non-spendable</p>
                )}
                {holdings.titles.map((t) => (
                  <div key={t.asset_id} data-testid="holding-deed" className="flex justify-between font-mono text-[11px] py-1 border-b border-stone-800/60">
                    <span className="text-stone-200">{t.parcel_apn} <span className="text-stone-500">· {t.county}</span></span>
                    <span className="text-amber-400">{t.share_pct}% ≈ ${t.title_value_usd.toLocaleString("en-US", { maximumFractionDigits: 2 })}</span>
                  </div>
                ))}
                {holdings.skrs.map((s) => (
                  <div key={s.skr_number} data-testid="holding-skr" className="flex justify-between font-mono text-[11px] py-1 border-b border-stone-800/60">
                    <span className="text-stone-200">{s.skr_number} <span className="text-stone-500">· {s.elements.join("/")}</span></span>
                    <span className="text-emerald-400">{s.net_pure_weight_grams}g vaulted</span>
                  </div>
                ))}
                <p className="text-[9px] text-stone-500 mt-2">Fractional allocations are beneficial interests in the BGNZN Trust estate, sealed on chain 9940 — the Trust holds title until distribution; SKRs attest vaulted metal backing.</p>
              </div>
            )}
            {covenantTape && (
              <div data-testid="covenant-tape" className="rounded-xl border border-stone-700 bg-stone-900/60 p-4">
                <p className="text-[10px] font-mono uppercase tracking-widest text-amber-500/80 mb-2">
                  Household tape · covenant {covenantTape.covenant.covenant_id} · with {covenantTape.covenant.partner_label || "co-parent"}
                </p>
                {covenantTape.pool?.available_equity_usd != null && (
                  <p data-testid="covenant-pool" className="font-mono text-[11px] text-emerald-400 mb-1">
                    child development pool: ${covenantTape.pool.available_equity_usd.toLocaleString("en-US", { maximumFractionDigits: 2 })} — funded by real deposits only
                  </p>
                )}
                {covenantTape.entries.length === 0 ? (
                  <p data-testid="covenant-tape-empty" className="text-xs text-stone-500 py-1">No maintenance settles yet — text /MAINTAIN [amount] [SUSD|AU|AG|PT|CU] to start the tape.</p>
                ) : covenantTape.entries.map((e, i) => (
                  <div key={i} data-testid="covenant-tape-row" className="flex justify-between font-mono text-[11px] py-1 border-b border-stone-800/60">
                    <span className={e.direction === "SENT" ? "text-stone-400" : "text-emerald-400"}>
                      {e.direction === "SENT" ? "→" : "←"} ${e.amount_usd.toLocaleString("en-US", { maximumFractionDigits: 2 })} {e.asset_label} · {e.counterparty || ""}
                    </span>
                    <span className="text-stone-500">block {e.block}</span>
                  </div>
                ))}
                <p className="text-[9px] text-stone-500 mt-2">Every entry is a sealed chain-9940 settlement between the two of you — 50/50 equilibrium, no third-party ledger.</p>
              </div>
            )}
            <WalletFundingSources api={API} token={token} merchantApproved={!!me?.merchant_approved} onLoaded={() => { loadMe(); loadCard(); }} />
            <WalletCardLoad api={API} token={token} onLoaded={() => { loadMe(); loadCard(); }} />
            {me?.merchant_approved && tape && (
              <div data-testid="wallet-sales-tape" className="rounded-xl border border-stone-700 bg-stone-900/60 p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-emerald-400/80">Today's tape · {tape.date}</p>
                  <p className="text-[9px] font-mono text-stone-500">your register · live</p>
                </div>
                {tape.sales.length === 0 ? (
                  <p data-testid="wallet-tape-empty" className="text-xs text-stone-500 text-center py-2">No buckets yet today — the register tape fills as sales settle.</p>
                ) : (
                  <>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {tape.sales.map((s, i) => (
                        <div key={s.settlement_tx} data-testid={`wallet-tape-row-${i}`} className="flex items-baseline justify-between gap-2 font-mono text-[11px]">
                          <span className="text-stone-500">{s.local_time}</span>
                          <span className="text-stone-200 truncate flex-1">{s.is_tip && <span className="text-sky-400 font-bold">TIP · </span>}{s.cardholder_label} <span className="text-stone-500">· {s.memo}</span></span>
                          <span className={`font-bold ${s.is_tip ? "text-sky-400" : "text-emerald-400"}`}>{fmtUsd(s.amount_usd)}</span>
                        </div>
                      ))}
                    </div>
                    <div data-testid="wallet-tape-totals" className="mt-2 pt-2 border-t border-stone-800 flex justify-between font-mono text-[10px] text-stone-400">
                      <span>{tape.totals.count} buckets</span>
                      <span className="text-emerald-400">{fmtUsd(tape.totals.gross_usd)} pts</span>
                      <span className="text-sky-400" data-testid="wallet-tape-tips">tips {fmtUsd(tape.totals.tips_usd || 0)}</span>
                      <span>avg {fmtUsd(tape.totals.avg_usd)}</span>
                      <span className="text-amber-400">high {fmtUsd(tape.totals.biggest_usd)}</span>
                    </div>
                  </>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <button
                data-testid="wallet-receive-button"
                onClick={() => { setMode("receive"); setPayRequest(null); setPayError(""); }}
                className="rounded-xl border border-stone-700 bg-stone-900/60 py-4 text-sm font-bold text-stone-100 hover:border-amber-500/50 transition-colors"
              >
                Receive ▼
              </button>
              <button
                data-testid="wallet-pay-button"
                onClick={() => { setMode("pay"); setScanned(null); setPayCode(""); setPayError(""); }}
                className="rounded-xl bg-amber-500 py-4 text-sm font-bold text-stone-950 hover:bg-amber-400 transition-colors"
              >
                Scan to Pay
              </button>
            </div>
            <a
              data-testid="appstore-install-button"
              href="https://apps.apple.com/app/id6753765418"
              target="_blank"
              rel="noopener noreferrer"
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-stone-700 bg-stone-900/60 py-4 text-sm font-bold text-stone-100 hover:border-amber-500/50 transition-colors"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08ZM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25Z"/>
              </svg>
              Get the iOS app
            </a>
            <button
              data-testid="fund-open-button"
              onClick={() => { setFundOpen(!fundOpen); setFundMsg(""); }}
              className="w-full rounded-xl border border-emerald-500/40 bg-emerald-950/20 py-4 text-sm font-bold text-emerald-300 hover:bg-emerald-950/40 transition-colors"
            >
              Fund your wallet — remotely or in person
            </button>
            {fundOpen && (
              <div data-testid="fund-panel" className="rounded-xl border border-stone-800 bg-stone-900/60 p-4 space-y-4">
                <div className="space-y-2">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-400">From your bank — Chase, Wells Fargo, BofA, any bank app</div>
                  <div className="grid grid-cols-2 gap-2">
                    {[["ZELLE", "Zelle — lands in minutes"], ["WIRE", "Wire / ACH — 1-3 days"]].map(([rail, label]) => (
                      <button
                        key={rail}
                        type="button"
                        data-testid={`fund-rail-${rail.toLowerCase()}`}
                        onClick={() => setFundRail(rail)}
                        className={`rounded-lg border px-2 py-2 text-[11px] font-bold uppercase tracking-wider transition-colors ${
                          fundRail === rail
                            ? "border-emerald-500/60 bg-emerald-950/40 text-emerald-300"
                            : "border-stone-700 bg-stone-950 text-stone-500 hover:text-stone-300"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {fundRail === "ZELLE" ? (
                    <div data-testid="zelle-coordinates" className="rounded-lg bg-stone-950 px-3 py-2 text-[11px] space-y-1">
                      {wireInfo?.zelle?.configured ? (
                        <>
                          <div className="text-stone-500 text-[9px] uppercase tracking-widest">Zelle the treasury desk at</div>
                          <div className="font-mono text-emerald-300 text-sm select-all">{wireInfo.zelle.handle}</div>
                        </>
                      ) : (
                        <p className="text-stone-500">
                          The desk's Zelle handle is issued by the treasury officer — ask at the portal.
                          Your reference below works either way.
                        </p>
                      )}
                      <p className="text-[10px] text-stone-500 leading-relaxed">
                        Open your Chase, Wells Fargo, or BofA app → Zelle → send to the desk's handle →
                        memo: <span className="text-amber-400">your reference below</span> (first 8 characters if the app limits you).
                      </p>
                    </div>
                  ) : (
                    wireInfo?.configured ? (
                      <div data-testid="wire-coordinates" className="rounded-lg bg-stone-950 px-3 py-2 text-[11px] font-mono text-stone-300 space-y-1">
                        <div>{wireInfo.coordinates.beneficiary_name}</div>
                        <div>{wireInfo.coordinates.bank_name}</div>
                        {wireInfo.coordinates.bank_address && <div>{wireInfo.coordinates.bank_address}</div>}
                        <div>Routing {wireInfo.coordinates.routing_number} · Acct {wireInfo.coordinates.account_number}</div>
                        {wireInfo.coordinates.swift && <div>SWIFT {wireInfo.coordinates.swift}</div>}
                        {wireInfo.coordinates.desk_contact && (
                          <div className="text-stone-500">Desk: {wireInfo.coordinates.desk_contact}</div>
                        )}
                      </div>
                    ) : (
                      <p className="text-[11px] text-stone-500">
                        The desk's receiving coordinates are issued by the treasury officer — ask at the
                        portal. Your reference below works either way.
                      </p>
                    )
                  )}
                  <div className="rounded-lg bg-stone-950 px-3 py-2">
                    <div className="text-[9px] uppercase tracking-widest text-stone-500">Your memo / reference — required</div>
                    <div data-testid="wire-reference" className="text-[11px] font-mono text-amber-400 break-all select-all">
                      {wireInfo?.reference_code || me?.ledger_uuid}
                    </div>
                  </div>
                  <form onSubmit={declareWire} className="flex gap-2">
                    <input
                      data-testid="wire-amount-input"
                      type="number" step="0.01" min="1" required placeholder={fundRail === "ZELLE" ? "Amount Zelled (USD)" : "Amount wired (USD)"}
                      value={fundAmount}
                      onChange={(e) => setFundAmount(e.target.value)}
                      className="flex-1 rounded-lg border border-stone-700 bg-stone-950 px-3 py-2.5 text-sm text-stone-100 placeholder:text-stone-600 focus:border-emerald-500 focus:outline-none"
                    />
                    <button
                      data-testid="wire-declare-button"
                      type="submit" disabled={fundBusy}
                      className="rounded-lg bg-emerald-500 px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-stone-950 hover:bg-emerald-400 disabled:opacity-50"
                    >
                      {fundBusy ? "…" : "Declare"}
                    </button>
                  </form>
                  <p className="text-[10px] text-stone-500 leading-relaxed">
                    Send from your retail account, then declare it here. The treasury desk
                    confirms receipt and your \$sUSD lands 1:1 — no cash changes hands anywhere public.
                  </p>
                </div>
                <div data-testid="apple-cash-rail" className="space-y-2 rounded-lg border border-emerald-500/25 bg-emerald-950/10 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-400">From Apple Cash — closed loop</div>
                  <ol className="text-[11px] text-stone-400 leading-relaxed space-y-1 list-decimal list-inside">
                    <li>Apple Cash card → <span className="text-stone-300">Transfer to Bank</span> (free ACH, or instant to your debit card).</li>
                    <li>From that bank, wire/ACH to the desk using <span className="text-amber-400">your reference above</span>.</li>
                    <li>The desk confirms receipt and your \$sUSD mints 1:1 into this wallet.</li>
                  </ol>
                  <p className="text-[10px] text-stone-500 leading-relaxed">
                    Apple exposes no pull API for Apple Cash — so the loop meets it at your bank.
                    Every dollar still enters through rails we own, and the reserve stays 1:1.
                  </p>
                </div>
                <div className="space-y-1">
                  <div className="text-[10px] uppercase tracking-widest text-stone-500">Notes at a custody window — worldwide</div>
                  <p className="text-[11px] text-stone-400 leading-relaxed">
                    Deposit physical notes at any active custody window on the network — any city,
                    any currency. Show your voucher, hand over the notes; the holder logs it from
                    their own wallet, you countersign below, and the $sUSD settles on-chain. Your
                    ledger, your chain (9940): no outside app can hold, freeze, or reverse your balance.
                  </p>
                  <button
                    data-testid="cashin-voucher-button"
                    onClick={showVoucher}
                    disabled={voucherBusy}
                    className="w-full rounded-lg border border-amber-500/50 py-3 text-xs font-bold text-amber-400 hover:bg-amber-500/10 disabled:opacity-50"
                  >
                    {voucherBusy ? "Issuing…" : "Global Cash Deposit — show my voucher"}
                  </button>
                  {voucher && (
                    <div data-testid="cashin-voucher" className="rounded-xl border border-amber-500/40 bg-stone-950 p-4 space-y-2 text-center">
                      <div className="mx-auto w-fit rounded-lg bg-white p-3">
                        <QRCodeSVG value={JSON.stringify(voucher)} size={170} />
                      </div>
                      <div className="text-[10px] font-mono text-amber-400 tracking-widest">{voucher.voucher_id}</div>
                      <p className="text-[10px] text-stone-500 leading-relaxed">
                        Single-use · valid 72h · the window scans or keys this code, you countersign
                        after they log it.
                      </p>
                    </div>
                  )}
                </div>
                {intentions.length > 0 && (
                  <div data-testid="funding-history" className="space-y-1.5">
                    <div className="text-[10px] uppercase tracking-widest text-stone-500">Your bank loads — status</div>
                    {intentions.slice(0, 5).map((it) => (
                      <div
                        key={it.intention_id}
                        data-testid={`funding-intention-${it.intention_id}`}
                        className="rounded-lg bg-stone-950 px-3 py-2 space-y-1"
                      >
                        <div className="flex items-center justify-between text-[11px]">
                          <span className="font-mono text-stone-400">
                            {it.intention_id}
                            <span className="ml-2 text-[9px] uppercase tracking-widest text-stone-600">{it.rail || "WIRE"}</span>
                          </span>
                          <span className="text-stone-300">{fmtUsd(it.amount_usd)}</span>
                          <span
                            data-testid={`funding-intention-${it.intention_id}-status`}
                            className={
                              it.status === "SETTLED" ? "text-emerald-400"
                              : it.status === "DISMISSED" ? "text-stone-600"
                              : "text-amber-400"
                            }
                          >
                            {it.status === "SETTLED" ? "settled — spendable"
                            : it.status === "DISMISSED" ? "dismissed by desk"
                            : "declared — awaiting bank"}
                          </span>
                        </div>
                        <div className="text-[9px] font-mono text-stone-600">
                          declared {new Date(it.declared_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                          {it.status === "SETTLED" && (
                            <span className="text-emerald-500/80">
                              {" "}· received {fmtUsd(it.amount_received_usd)} · block #{it.settlement_tx?.block} — your receipt text has the details
                            </span>
                          )}
                          {it.status === "DECLARED" && (
                            <span> · the desk matches your wire by amount + your reference above</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {fundMsg && <p data-testid="fund-message" className="text-xs text-emerald-300/90 text-center">{fundMsg}</p>}
              </div>
            )}
            {me && !me.passkey_enrolled && (
              <button
                data-testid="passkey-enable-button"
                onClick={enrollPasskey}
                disabled={passkeyBusy}
                className="w-full rounded-xl border border-amber-500/40 bg-stone-900/60 py-4 text-sm font-bold text-amber-300 hover:border-amber-400 transition-colors disabled:opacity-50"
              >
                {passkeyBusy ? "Waiting for Face ID…" : "Enable Face ID payment approval"}
              </button>
            )}
            {me?.passkey_enrolled && (
              <p data-testid="passkey-active-note" className="text-center text-[10px] uppercase tracking-widest text-emerald-400">
                Face ID payment approval active
              </p>
            )}
            {passkeyMsg && (
              <p data-testid="passkey-message" className="text-xs text-amber-300/90 leading-relaxed text-center">
                {passkeyMsg}
              </p>
            )}
          </>
        )}

        {/* receive */}
        {mode === "receive" && (
          <div data-testid="wallet-receive-panel" className="rounded-2xl border border-stone-800 bg-stone-900/60 p-6 space-y-4">
            <h2 className="text-sm font-bold text-stone-100">Receive cash</h2>
            {deepLinkNote && (
              <div data-testid="deep-link-banner" className="rounded-lg border border-amber-500/50 bg-amber-500/10 px-4 py-3 text-xs text-amber-300">
                {deepLinkNote}
              </div>
            )}
            {!payRequest ? (
              <form onSubmit={createRequest} className="space-y-3">
                <input
                  data-testid="receive-amount-input"
                  type="number" step="0.01" min="0.01" placeholder="Amount in USD (optional)"
                  value={recvForm.amount}
                  onChange={(e) => setRecvForm({ ...recvForm, amount: e.target.value })}
                  className="w-full rounded-lg border border-stone-700 bg-stone-950 px-4 py-3 text-sm text-stone-100 focus:border-amber-500 focus:outline-none"
                />
                <input
                  data-testid="receive-memo-input"
                  type="text" placeholder="What's it for? (optional)"
                  value={recvForm.memo}
                  onChange={(e) => setRecvForm({ ...recvForm, memo: e.target.value })}
                  className="w-full rounded-lg border border-stone-700 bg-stone-950 px-4 py-3 text-sm text-stone-100 focus:border-amber-500 focus:outline-none"
                />
                <button
                  data-testid="receive-create-button"
                  type="submit" disabled={busy}
                  className="w-full rounded-lg bg-amber-500 py-3 text-sm font-bold uppercase tracking-widest text-stone-950 hover:bg-amber-400 disabled:opacity-50"
                >
                  {busy ? "Minting code…" : "Show my code"}
                </button>
              </form>
            ) : (
              <div className="space-y-4 text-center">
                <div className="mx-auto w-fit rounded-xl bg-white p-4">
                  <QRCodeSVG
                    data-testid="receive-qr"
                    value={`${window.location.origin}/wallet?pay=${payRequest.code}`}
                    size={200}
                  />
                </div>
                <div data-testid="receive-code" className="text-2xl font-mono font-bold tracking-[0.3em] text-amber-400">
                  {payRequest.code}
                </div>
                <p className="text-xs text-stone-400">
                  {payRequest.amount_usd ? `${fmtUsd(payRequest.amount_usd)} · ` : ""}
                  {payRequest.memo || "Open amount"} — let the payer scan or enter this code.
                </p>
                <a
                  data-testid="receive-text-button"
                  href={`sms:?&body=${encodeURIComponent(
                    `${me?.label} requests ${payRequest.amount_usd ? fmtUsd(payRequest.amount_usd) + " " : ""}on BMEX. Open your wallet and tap this link (or enter code ${payRequest.code}): ${window.location.origin}/wallet?pay=${payRequest.code}`
                  )}`}
                  className="flex w-full items-center justify-center gap-2 rounded-lg border border-emerald-500/40 bg-emerald-950/20 py-3 text-xs font-bold text-emerald-300 hover:bg-emerald-950/40 transition-colors"
                >
                  Send as text — their Messages app carries it, no SMS gateway
                </a>
                {me?.ledger_uuid === "55dec7ab-7c2a-45af-88e5-0c6d6030522f" && (
                  <div className="rounded-xl border border-[#c9a06c]/40 bg-[#0c0b09] p-4 space-y-3" data-testid="storefront-share-panel">
                    <p className="text-[10px] font-mono tracking-widest uppercase text-[#c9a06c]">Dior's Dough storefront — for customers</p>
                    <div className="mx-auto w-fit rounded-xl bg-white p-3">
                      <QRCodeSVG
                        data-testid="storefront-qr"
                        value={`${window.location.origin}/pay/diorsdough?req=${payRequest.code}`}
                        size={150}
                      />
                    </div>
                    <p className="text-[11px] text-stone-400">
                      Customers open this — her branded page, this order pre-priced.
                    </p>
                    <div className="flex gap-2">
                      <button
                        data-testid="storefront-copy-button"
                        onClick={() => {
                          navigator.clipboard?.writeText(`${window.location.origin}/pay/diorsdough?req=${payRequest.code}`);
                          setCopied(true);
                          setTimeout(() => setCopied(false), 2000);
                        }}
                        className="flex-1 rounded-lg border border-[#c9a06c]/50 bg-[#c9a06c]/10 py-2.5 text-xs font-bold text-[#ecd9b8] hover:bg-[#c9a06c]/20 transition-colors"
                      >
                        {copied ? "Copied ✓" : "Copy customer link"}
                      </button>
                      <a
                        data-testid="storefront-text-button"
                        href={`sms:?&body=${encodeURIComponent(
                          `Dior's Dough — your order${payRequest.amount_usd ? ` (${fmtUsd(payRequest.amount_usd)})` : ""} is ready to pay: ${window.location.origin}/pay/diorsdough?req=${payRequest.code}`
                        )}`}
                        className="flex-1 rounded-lg border border-emerald-500/40 bg-emerald-950/20 py-2.5 text-center text-xs font-bold text-emerald-300 hover:bg-emerald-950/40 transition-colors"
                      >
                        Text it
                      </a>
                    </div>
                    <p className="text-[10px] text-center text-stone-500">
                      Instagram bio link: <span className="font-mono text-stone-400">{window.location.origin}/pay/diorsdough</span>
                    </p>
                  </div>
                )}
                <button
                  data-testid="receive-new-button"
                  onClick={() => setPayRequest(null)}
                  className="text-xs text-stone-400 underline hover:text-stone-200"
                >
                  New code
                </button>
              </div>
            )}
            {me?.merchant_approved && (
              <div className="rounded-xl border border-[#c9a227]/40 bg-[#0a1a33]/60 p-4 space-y-3 text-left" data-testid="charge-panel">
                <p className="text-[10px] font-mono tracking-widest uppercase text-[#e6c765]">Merchant — member card desk</p>
                <div className="grid grid-cols-2 gap-1 rounded-lg border border-stone-700 p-1" data-testid="charge-mode-tabs">
                  {[["charge", "Charge (pull)"], ["load", "Load (fill)"]].map(([m, label]) => (
                    <button
                      key={m}
                      data-testid={`charge-mode-${m}`}
                      onClick={() => { setChargeMode(m); setCharge((c) => ({ ...c, result: null, error: "" })); }}
                      className="rounded-md py-2 text-[10px] font-bold uppercase tracking-widest transition-colors"
                      style={chargeMode === m ? { background: "#e6c765", color: "#0a1a33" } : { color: "#a8a29e" }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-stone-400 leading-relaxed">
                  {chargeMode === "charge"
                    ? "Member shows their BMEX card QR — you scan, enter the amount, and it pulls from their balance instantly (balance-only). They get a texted receipt."
                    : "Member hands you cash / Apple Cash / any value — you fill their card 1:1. Every $100 loaded earns them a free dozen rolls or fruit; every $10,000 earns an acre + 3 fruit trees."}
                </p>
                {charge.result ? (
                  <div className="rounded-lg border border-emerald-500/50 bg-emerald-950/30 px-4 py-3 text-center space-y-1" data-testid="charge-result">
                    <p className="text-sm font-bold text-emerald-300">
                      {chargeMode === "charge" ? "Charged" : "Loaded"} {fmtUsd(charge.result.amount_usd)}
                    </p>
                    <p className="text-xs text-stone-300">
                      {chargeMode === "charge" ? `from ${charge.result.cardholder}'s card` : `onto ${charge.result.cardholder}'s card`}
                    </p>
                    {(charge.result.rewards_earned || []).map((r, i) => (
                      <p key={i} className="text-xs font-bold" style={{ color: "#e6c765" }} data-testid={`charge-reward-${i}`}>
                        ★ Reward unlocked: {r.label}
                      </p>
                    ))}
                    <p className="text-[10px] font-mono text-stone-500 break-all">settled · {charge.result.settlement_tx}</p>
                    <button
                      data-testid="charge-again-button"
                      onClick={() => setCharge({ token: "", amount: "", memo: "", busy: false, result: null, error: "" })}
                      className="text-xs text-stone-400 underline hover:text-stone-200 pt-1"
                    >
                      {chargeMode === "charge" ? "Charge another" : "Load another"}
                    </button>
                  </div>
                ) : (
                  <form onSubmit={submitCharge} className="space-y-2.5">
                    <div id="charge-qr-reader" data-testid="charge-qr-reader" className="overflow-hidden rounded-lg" />
                    {!chargeScanning ? (
                      <button
                        type="button"
                        data-testid="charge-scan-button"
                        onClick={startChargeScanner}
                        className="w-full rounded-lg border border-[#c9a227]/50 py-2.5 text-xs font-bold text-[#e6c765] hover:bg-[#c9a227]/10"
                      >
                        Scan member card
                      </button>
                    ) : (
                      <button
                        type="button"
                        data-testid="charge-scan-stop"
                        onClick={stopChargeScanner}
                        className="w-full rounded-lg border border-stone-700 py-2.5 text-xs text-stone-300"
                      >
                        Stop camera
                      </button>
                    )}
                    <input
                      data-testid="charge-token-input"
                      type="text" required placeholder="Card token (scanned or pasted)"
                      value={charge.token}
                      onChange={(e) => setCharge({ ...charge, token: e.target.value.replace(/^bmex:card:/i, "") })}
                      className="w-full rounded-lg border border-stone-700 bg-stone-950 px-4 py-3 text-xs font-mono text-stone-100 focus:border-amber-500 focus:outline-none"
                    />
                    <input
                      data-testid="charge-amount-input"
                      type="number" step="0.01" min="0.01" required placeholder="Amount (USD)"
                      value={charge.amount}
                      onChange={(e) => setCharge({ ...charge, amount: e.target.value })}
                      className="w-full rounded-lg border border-stone-700 bg-stone-950 px-4 py-3 text-sm text-stone-100 focus:border-amber-500 focus:outline-none"
                    />
                    <input
                      data-testid="charge-memo-input"
                      type="text" placeholder="What's it for? (optional)"
                      value={charge.memo}
                      onChange={(e) => setCharge({ ...charge, memo: e.target.value })}
                      className="w-full rounded-lg border border-stone-700 bg-stone-950 px-4 py-3 text-sm text-stone-100 focus:border-amber-500 focus:outline-none"
                    />
                    {charge.error && <p className="text-xs text-red-400" data-testid="charge-error">{charge.error}</p>}
                    <button
                      data-testid="charge-submit-button"
                      type="submit" disabled={charge.busy}
                      className="w-full rounded-lg py-3 text-xs font-bold uppercase tracking-widest text-[#0a1a33] disabled:opacity-50"
                      style={{ background: "#e6c765" }}
                    >
                      {charge.busy ? "Working…" : chargeMode === "charge" ? "Charge card" : "Load card"}
                    </button>
                  </form>
                )}
              </div>
            )}
            <button onClick={() => setMode("card")} className="text-xs text-stone-500 hover:text-stone-300">
              ← Back
            </button>
          </div>
        )}

        {/* pay */}
        {mode === "pay" && (
          <div data-testid="wallet-pay-panel" className="rounded-2xl border border-stone-800 bg-stone-900/60 p-6 space-y-4">
            <h2 className="text-sm font-bold text-stone-100">Pay someone</h2>
            {deepLinkNote && (
              <div data-testid="deep-link-banner" className="rounded-lg border border-amber-500/50 bg-amber-500/10 px-4 py-3 text-xs text-amber-300">
                {deepLinkNote}
              </div>
            )}
            <div id="wallet-qr-reader" data-testid="wallet-qr-reader" className="overflow-hidden rounded-lg" />
            {!scanning ? (
              <button
                data-testid="scan-start-button"
                onClick={startScanner}
                className="w-full rounded-lg border border-amber-500/50 py-3 text-sm font-bold text-amber-400 hover:bg-amber-500/10"
              >
                Open camera scanner
              </button>
            ) : (
              <button
                data-testid="scan-stop-button"
                onClick={stopScanner}
                className="w-full rounded-lg border border-stone-700 py-3 text-sm text-stone-300"
              >
                Stop scanner
              </button>
            )}
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-stone-600">
              <span className="h-px flex-1 bg-stone-800" /> or enter code <span className="h-px flex-1 bg-stone-800" />
            </div>
            <div className="flex gap-2">
              <input
                data-testid="pay-code-input"
                type="text" placeholder="8-character code"
                value={payCode}
                onChange={(e) => setPayCode(e.target.value.toUpperCase())}
                className="flex-1 rounded-lg border border-stone-700 bg-stone-950 px-4 py-3 font-mono text-sm tracking-widest text-stone-100 focus:border-amber-500 focus:outline-none"
              />
              <button
                data-testid="pay-code-submit"
                onClick={() => fetchRequest(payCode)}
                className="rounded-lg bg-amber-500 px-5 text-sm font-bold text-stone-950 hover:bg-amber-400"
              >
                Look up
              </button>
            </div>
            {payError && <p data-testid="pay-error" className="text-xs text-red-400">{payError}</p>}
            {passkeyRecovery && (
              <div data-testid="passkey-recovery" className="rounded-lg border border-violet-400/40 bg-violet-400/10 px-4 py-3 text-xs text-violet-200 space-y-2.5">
                <p className="leading-relaxed">
                  Your Face ID key was created on a different phone or browser — passkeys never leave
                  the device that made them. Set one up on this phone (one tap), then approve the payment again.
                </p>
                <button
                  data-testid="passkey-recovery-enroll"
                  onClick={async () => { await enrollPasskey(); setPasskeyRecovery(false); }}
                  className="rounded-full bg-violet-400 text-black font-bold uppercase tracking-widest text-[10px] px-4 py-2 hover:bg-violet-300 transition-colors"
                >
                  Set up Face ID on this phone
                </button>
              </div>
            )}

            {/* pay a phone number — the Apple Cash push twin */}
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-stone-600">
              <span className="h-px flex-1 bg-stone-800" /> or pay a phone number <span className="h-px flex-1 bg-stone-800" />
            </div>
            <div data-testid="phone-pay-block" className="space-y-2">
              {!phonePay.resolved ? (
                <>
                  <input
                    data-testid="phone-pay-number-input"
                    type="text" placeholder="Their mobile number or wallet address (0x…)"
                    value={phonePay.phone}
                    onChange={(e) => setPhonePay({ ...phonePay, phone: e.target.value, resolved: null })}
                    className="w-full rounded-lg border border-stone-700 bg-stone-950 px-4 py-3 text-sm text-stone-100 placeholder:text-stone-600 focus:border-amber-500 focus:outline-none"
                  />
                  <input
                    data-testid="phone-pay-amount-input"
                    type="number" step="0.01" min="0.01" placeholder="Amount (USD)"
                    value={phonePay.amount}
                    onChange={(e) => setPhonePay({ ...phonePay, amount: e.target.value })}
                    className="w-full rounded-lg border border-stone-700 bg-stone-950 px-4 py-3 text-sm text-stone-100 placeholder:text-stone-600 focus:border-amber-500 focus:outline-none"
                  />
                  <button
                    data-testid="phone-pay-lookup-button"
                    onClick={resolvePhone}
                    disabled={phoneBusy || !phonePay.phone || !phonePay.amount}
                    className="w-full rounded-lg border border-amber-500/50 py-3 text-sm font-bold text-amber-400 hover:bg-amber-500/10 disabled:opacity-50"
                  >
                    {phoneBusy ? "Looking up…" : "Look up recipient"}
                  </button>
                </>
              ) : (
                <div className="rounded-lg border border-amber-500/40 bg-stone-950 p-4 space-y-3">
                  <div className="text-center">
                    <div className="text-[10px] uppercase tracking-widest text-stone-500">Sending to</div>
                    <div data-testid="phone-pay-recipient" className="text-lg font-bold text-stone-100">{phonePay.resolved.label}</div>
                    <div className="text-xs text-stone-500">{phonePay.resolved.phone_masked} · {fmtUsd(parseFloat(phonePay.amount))}</div>
                  </div>
                  <button
                    data-testid="phone-pay-send-button"
                    onClick={sendPhonePay}
                    disabled={phoneBusy}
                    className="w-full rounded-lg bg-amber-500 py-3 text-sm font-bold uppercase tracking-widest text-stone-950 hover:bg-amber-400 disabled:opacity-50"
                  >
                    {phoneBusy ? (me?.passkey_enrolled ? "Waiting for Face ID…" : "Sending…") : "Send now"}
                  </button>
                  <button
                    data-testid="phone-pay-back-button"
                    onClick={() => setPhonePay({ ...phonePay, resolved: null })}
                    className="w-full text-xs text-stone-500 underline hover:text-stone-300"
                  >
                    Different recipient
                  </button>
                </div>
              )}
            </div>
            <button onClick={() => { stopScanner(); setMode("card"); }} className="text-xs text-stone-500 hover:text-stone-300">
              ← Back
            </button>
          </div>
        )}

        {/* confirm */}
        {mode === "confirm" && scanned && (
          <div data-testid="wallet-confirm-panel" className="rounded-2xl border border-amber-500/40 bg-stone-900/60 p-6 space-y-4">
            <h2 className="text-sm font-bold text-stone-100">Confirm payment</h2>
            <div className="rounded-lg bg-stone-950 p-4 space-y-1 text-sm">
              <div className="text-stone-400 text-xs">Paying</div>
              <div className="flex items-center gap-3">
                {scanned.payee_brand?.logo && (
                  <img
                    data-testid="confirm-payee-logo"
                    src={scanned.payee_brand.logo}
                    alt={scanned.payee_label}
                    className="h-10 w-10 rounded-full border border-amber-500/40 object-cover"
                  />
                )}
                <div data-testid="confirm-payee" className="text-lg font-bold text-stone-100">{scanned.payee_label}</div>
              </div>
              {scanned.memo && <div className="text-xs text-stone-500">"{scanned.memo}"</div>}
            </div>
            {scanned.amount_usd != null ? (
              <div data-testid="confirm-amount" className="text-3xl font-extrabold text-amber-400 display-serif">
                {fmtUsd(scanned.amount_usd)}
              </div>
            ) : (
              <input
                data-testid="confirm-amount-input"
                type="number" step="0.01" min="0.01" placeholder="Enter amount (USD)"
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
                className="w-full rounded-lg border border-stone-700 bg-stone-950 px-4 py-3 text-lg font-bold text-stone-100 focus:border-amber-500 focus:outline-none"
              />
            )}
            {payError && <p data-testid="confirm-error" className="text-xs text-red-400">{payError}</p>}
            {passkeyRecovery && (
              <div data-testid="passkey-recovery" className="rounded-lg border border-violet-400/40 bg-violet-400/10 px-4 py-3 text-xs text-violet-200 space-y-2.5">
                <p className="leading-relaxed">
                  Your Face ID key was created on a different phone or browser — passkeys never leave
                  the device that made them. Set one up on this phone (one tap), then approve the payment again.
                </p>
                <button
                  data-testid="passkey-recovery-enroll"
                  onClick={async () => { await enrollPasskey(); setPasskeyRecovery(false); }}
                  className="rounded-full bg-violet-400 text-black font-bold uppercase tracking-widest text-[10px] px-4 py-2 hover:bg-violet-300 transition-colors"
                >
                  Set up Face ID on this phone
                </button>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <button
                data-testid="confirm-cancel-button"
                onClick={() => { setScanned(null); setMode("pay"); }}
                className="rounded-lg border border-stone-700 py-3 text-sm text-stone-300"
              >
                Cancel
              </button>
              <button
                data-testid="confirm-pay-button"
                onClick={executePay}
                disabled={busy}
                className="rounded-lg bg-amber-500 py-3 text-sm font-bold uppercase tracking-widest text-stone-950 hover:bg-amber-400 disabled:opacity-50"
              >
                {busy ? "Settling…" : me?.passkey_enrolled ? "Approve with Face ID" : "Pay now"}
              </button>
            </div>
          </div>
        )}

        {/* receipt */}
        {mode === "receipt" && receipt && (
          <div data-testid="wallet-receipt-panel" className="rounded-2xl border border-emerald-500/40 bg-stone-900/60 p-6 space-y-3 text-center">
            <div className="text-3xl">✓</div>
            <h2 className="text-sm font-bold text-emerald-400 uppercase tracking-widest">Settled T+0</h2>
            <div data-testid="receipt-amount" className="text-3xl font-extrabold text-stone-100 display-serif">
              {fmtUsd(receipt.amount_usd)}
            </div>
            <p className="text-xs text-stone-400">
              New balance: <span className="text-emerald-400">{fmtUsd(receipt.sender_susd_balance)}</span>
            </p>
            <p data-testid="receipt-tx" className="break-all font-mono text-[10px] text-stone-600">
              tx {receipt.settlement_tx?.tx} · block #{receipt.settlement_tx?.block}
            </p>
            <button
              data-testid="receipt-done-button"
              onClick={() => { setReceipt(null); setMode("card"); }}
              className="rounded-lg bg-amber-500 px-8 py-3 text-sm font-bold text-stone-950 hover:bg-amber-400"
            >
              Done
            </button>
          </div>
        )}

        {/* custody desk — physical cash in/out */}
        {custody && (
          <div data-testid="wallet-custody-panel" className="space-y-2">
            <h3 className="text-[10px] uppercase tracking-widest text-stone-500">Custody windows</h3>

            {custody.receipts?.filter((r) => r.status === "PENDING_MEMBER_CONFIRM").map((r) => (
              <div
                key={r.receipt_id}
                data-testid={`custody-pending-${r.receipt_id}`}
                className="rounded-lg border border-amber-500/40 bg-amber-950/20 px-4 py-3 space-y-2"
              >
                <div className="text-xs text-stone-200">
                  {r.custody_point_label} attests it received <span className="font-bold text-amber-400">{fmtUsd(r.amount_usd)}</span> cash from you
                </div>
                <button
                  data-testid={`custody-confirm-${r.receipt_id}`}
                  onClick={() => confirmDeposit(r.receipt_id)}
                  disabled={custodyBusy}
                  className="w-full rounded-lg bg-amber-500 py-2.5 text-xs font-bold uppercase tracking-widest text-stone-950 hover:bg-amber-400 disabled:opacity-50"
                >
                  {custodyBusy ? "Signing…" : "Countersign & settle"}
                </button>
              </div>
            ))}

            {!cashOut.open ? (
              <button
                data-testid="cashout-open-button"
                onClick={() => setCashOut({ ...cashOut, open: true })}
                className="w-full rounded-xl border border-stone-700 bg-stone-900/60 py-3 text-sm font-bold text-stone-100 hover:border-amber-500/50 transition-colors"
              >
                Redeem — collect at a custody window
              </button>
            ) : (
              <form
                data-testid="cashout-form"
                onSubmit={submitCashOut}
                className="rounded-xl border border-stone-800 bg-stone-900/60 p-4 space-y-3"
              >
                <select
                  data-testid="cashout-point-select"
                  required
                  value={cashOut.point_id}
                  onChange={(e) => setCashOut({ ...cashOut, point_id: e.target.value })}
                  className="w-full rounded-lg border border-stone-700 bg-stone-950 px-4 py-3 text-sm text-stone-100 focus:border-amber-500 focus:outline-none"
                >
                  <option value="">Choose a custody point…</option>
                  {custodyPoints.map((p) => (
                    <option key={p.point_id} value={p.point_id}>
                      {p.label} ({p.point_type})
                    </option>
                  ))}
                </select>
                <input
                  data-testid="cashout-amount-input"
                  type="number" step="0.01" min="0.01" required placeholder="Amount (USD)"
                  value={cashOut.amount}
                  onChange={(e) => setCashOut({ ...cashOut, amount: e.target.value })}
                  className="w-full rounded-lg border border-stone-700 bg-stone-950 px-4 py-3 text-sm text-stone-100 focus:border-amber-500 focus:outline-none"
                />
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setCashOut({ open: false, point_id: "", amount: "" })}
                    className="rounded-lg border border-stone-700 py-2.5 text-xs text-stone-300"
                  >
                    Cancel
                  </button>
                  <button
                    data-testid="cashout-submit-button"
                    type="submit" disabled={custodyBusy}
                    className="rounded-lg bg-amber-500 py-2.5 text-xs font-bold uppercase tracking-widest text-stone-950 hover:bg-amber-400 disabled:opacity-50"
                  >
                    {custodyBusy ? "Burning…" : "Redeem"}
                  </button>
                </div>
              </form>
            )}

            {custodyMsg && (
              <p data-testid="custody-message" className="text-xs text-amber-300/90 leading-relaxed">{custodyMsg}</p>
            )}

            {custody.receipts?.filter((r) => r.status !== "PENDING_MEMBER_CONFIRM").slice(0, 5).map((r) => (
              <div
                key={r.receipt_id}
                data-testid={`custody-row-${r.receipt_id}`}
                className="flex items-center justify-between rounded-lg border border-stone-800 bg-stone-900/40 px-4 py-2.5"
              >
                <div>
                  <div className="text-xs text-stone-300">
                    {r.direction === "CASH_IN" ? "Cash deposited" : "Cash collected"} · {r.custody_point_label}
                  </div>
                  <div className="text-[10px] text-stone-600 font-mono">{r.receipt_id} · {r.status.replaceAll("_", " ")}</div>
                </div>
                <span className={`text-sm font-bold ${r.direction === "CASH_IN" ? "text-emerald-400" : "text-stone-300"}`}>
                  {r.direction === "CASH_IN" ? "+" : "−"}{fmtUsd(r.amount_usd)}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* member dues — straight to the director's treasury */}
        <div data-testid="dues-panel" className="space-y-2">
          <h3 className="text-[10px] uppercase tracking-widest text-stone-500">Member dues</h3>
          <form
            data-testid="dues-form"
            onSubmit={payDues}
            className="rounded-xl border border-stone-800 bg-stone-900/60 p-4 space-y-3"
          >
            <p className="text-[11px] text-stone-400 leading-relaxed">
              Dues settle instantly into the director's treasury — receipt posts to your history.
            </p>
            <input
              data-testid="dues-amount-input"
              type="number" step="0.01" min="0.01" required placeholder="Amount (USD)"
              value={dues.amount}
              onChange={(e) => setDues({ ...dues, amount: e.target.value })}
              className="w-full rounded-lg border border-stone-700 bg-stone-950 px-4 py-3 text-sm text-stone-100 focus:border-amber-500 focus:outline-none"
            />
            <button
              data-testid="dues-pay-button"
              type="submit" disabled={dues.busy}
              className="w-full rounded-lg bg-amber-500 py-3 text-xs font-bold uppercase tracking-widest text-stone-950 hover:bg-amber-400 disabled:opacity-50"
            >
              {dues.busy ? "Settling…" : "Pay dues"}
            </button>
            {dues.msg && (
              <p data-testid="dues-message" className="text-xs text-amber-300/90 leading-relaxed">{dues.msg}</p>
            )}
          </form>
        </div>

        {/* merchant cash-out — the wallet debits on-chain at once; the desk sends it by Zelle */}
        {me?.merchant_approved && (
          <div data-testid="wireout-panel" className="space-y-2">
            <h3 className="text-[10px] uppercase tracking-widest text-stone-500">Cash out to my bank</h3>
            {!wireOut.open ? (
              <button
                data-testid="wireout-open-button"
                onClick={() => setWireOut({ ...wireOut, open: true })}
                className="w-full rounded-xl border border-stone-700 bg-stone-900/60 py-3 text-sm font-bold text-stone-100 hover:border-amber-500/50 transition-colors"
              >
                Wire out to my bank
              </button>
            ) : (
              <form
                data-testid="wireout-form"
                onSubmit={submitWireOut}
                className="rounded-xl border border-stone-800 bg-stone-900/60 p-4 space-y-3"
              >
                <p className="text-[11px] text-stone-400 leading-relaxed">
                  Queue a redemption to your bank. The rail settles through the exchange's outgoing
                  transfer channel — requests placed now execute when it opens.
                </p>
                <input
                  data-testid="wireout-bank-input"
                  type="text" required placeholder="Bank label (e.g. Chase ****4821) — never the full account number"
                  value={wireOut.bank_label}
                  onChange={(e) => setWireOut({ ...wireOut, bank_label: e.target.value })}
                  className="w-full rounded-lg border border-stone-700 bg-stone-950 px-4 py-3 text-sm text-stone-100 focus:border-amber-500 focus:outline-none"
                />
                <input
                  data-testid="wireout-amount-input"
                  type="number" step="0.01" min="0.01" required placeholder="Amount (USD)"
                  value={wireOut.amount}
                  onChange={(e) => setWireOut({ ...wireOut, amount: e.target.value })}
                  className="w-full rounded-lg border border-stone-700 bg-stone-950 px-4 py-3 text-sm text-stone-100 focus:border-amber-500 focus:outline-none"
                />
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setWireOut({ open: false, amount: "", bank_label: "" })}
                    className="rounded-lg border border-stone-700 py-2.5 text-xs text-stone-300"
                  >
                    Cancel
                  </button>
                  <button
                    data-testid="wireout-submit-button"
                    type="submit" disabled={wireOutBusy}
                    className="rounded-lg bg-amber-500 py-2.5 text-xs font-bold uppercase tracking-widest text-stone-950 hover:bg-amber-400 disabled:opacity-50"
                  >
                    {wireOutBusy ? "Queueing…" : "Queue wire-out"}
                  </button>
                </div>
              </form>
            )}
            {wireOutMsg && (
              <p data-testid="wireout-message" className="text-xs text-amber-300/90 leading-relaxed">{wireOutMsg}</p>
            )}
            {wireOuts.slice(0, 5).map((w) => (
              <div
                key={w.request_id}
                data-testid={`wireout-row-${w.request_id}`}
                className="flex items-center justify-between rounded-lg border border-stone-800 bg-stone-900/40 px-4 py-2.5"
              >
                <div>
                  <div className="text-xs text-stone-300">Wire-out · {w.bank_label}</div>
                  <div className="text-[10px] text-stone-600 font-mono">{w.request_id} · {String(w.status).replaceAll("_", " ")}</div>
                </div>
                <span className="text-sm font-bold text-stone-300">−{fmtUsd(w.amount_usd)}</span>
              </div>
            ))}
          </div>
        )}

        {/* direct bank rail — instant cash-out for every member the day the whitelist clears */}
        <div data-testid="cashout-panel" className="space-y-2">
          <h3 className="text-[10px] uppercase tracking-widest text-stone-500">Direct bank rail</h3>
          {!cashRail.live ? (
            <div className="rounded-xl border border-stone-800 bg-stone-900/40 px-4 py-3 space-y-1">
              <p className="text-[11px] text-stone-500 leading-relaxed">
                Instant cash-out: your $sUSD settles back to the treasury on-chain and the bank pays
                out in one move — no queue. The rail arms the moment the bank clears this house's
                address; until then, wire-out queueing stays available{me?.merchant_approved ? " above" : " for merchant accounts"}.
              </p>
              <p data-testid="cashout-status" className="font-mono text-[10px] text-amber-500/80">RAIL: PENDING WHITELIST</p>
            </div>
          ) : !cashOutForm.open ? (
            <button
              data-testid="cashout-open-button"
              onClick={() => setCashOutForm({ open: true, amount: "" })}
              className="w-full rounded-xl border border-amber-500/50 bg-stone-900/60 py-3 text-sm font-bold text-amber-300 hover:bg-amber-500/10 transition-colors"
            >
              Cash out — instant
            </button>
          ) : (
            <form
              data-testid="cashout-form"
              onSubmit={submitRailCashOut}
              className="rounded-xl border border-amber-500/40 bg-stone-900/60 p-4 space-y-3"
            >
              <p className="text-[11px] text-stone-400 leading-relaxed">
                The debit settles on-chain immediately and the bank rail pays out. If the bank leg
                ever faults, your balance is refunded in full in the same breath — nothing is ever
                left in between.
              </p>
              <input
                data-testid="cashout-amount-input"
                type="number" step="0.01" min="0.01" required placeholder="Amount (USD)"
                value={cashOutForm.amount}
                onChange={(e) => setCashOutForm({ ...cashOutForm, amount: e.target.value })}
                className="w-full rounded-lg border border-stone-700 bg-stone-950 px-4 py-3 text-sm text-stone-100 focus:border-amber-500 focus:outline-none"
              />
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setCashOutForm({ open: false, amount: "" })}
                  className="rounded-lg border border-stone-700 py-2.5 text-xs text-stone-300"
                >
                  Cancel
                </button>
                <button
                  data-testid="cashout-submit-button"
                  type="submit" disabled={cashOutBusy}
                  className="rounded-lg bg-amber-500 py-2.5 text-xs font-bold uppercase tracking-widest text-stone-950 hover:bg-amber-400 disabled:opacity-50"
                >
                  {cashOutBusy ? "Settling…" : "Cash out now"}
                </button>
              </div>
            </form>
          )}
          {cashOutMsg && (
            <p data-testid="cashout-message" className="text-xs text-amber-300/90 leading-relaxed">{cashOutMsg}</p>
          )}
          {cashRail.cashouts.slice(0, 5).map((c) => (
            <div
              key={c.payout_id}
              data-testid={`cashout-row-${c.payout_id}`}
              className="flex items-center justify-between rounded-lg border border-stone-800 bg-stone-900/40 px-4 py-2.5"
            >
              <div>
                <div className="text-xs text-stone-300">Cash-out · block #{c.block ?? "—"}</div>
                <div className="text-[10px] text-stone-600 font-mono">{c.payout_id} · {String(c.status).replaceAll("_", " ")}</div>
              </div>
              <span className="text-sm font-bold text-stone-300">−{fmtUsd(c.amount_usd)}</span>
            </div>
          ))}
        </div>

        {/* custodian cash window — only for members who HOLD a custody point */}
        {desk?.held_points?.length > 0 && (
          <div data-testid="custodian-desk-panel" className="space-y-2">
            <h3 className="text-[10px] uppercase tracking-widest text-stone-500">
              Your cash window — {desk.held_points.map((p) => p.label).join(", ")}
            </h3>
            <form
              data-testid="custodian-deposit-form"
              onSubmit={deskAttest}
              className="rounded-xl border border-stone-800 bg-stone-900/60 p-4 space-y-3"
            >
              <p className="text-[11px] text-stone-400 leading-relaxed">
                Taken delivery of value — notes across the counter, or a transfer you personally
                vouch for? Log it here: they countersign from their wallet and the $sUSD settles to
                them on-chain. Dual-signed, nothing moves on trust.
              </p>
              <input
                data-testid="custodian-member-uuid-input"
                type="text" required placeholder="Depositor's ledger UUID — or paste their voucher"
                value={deskForm.member_uuid}
                onChange={(e) => {
                  const raw = e.target.value;
                  const trimmed = raw.trim();
                  if (trimmed.startsWith("{")) {
                    try {
                      const v = JSON.parse(trimmed);
                      if (v.protocol === "BMEX_GLOBAL_CASHIN_V1") {
                        setDeskForm({ ...deskForm, member_uuid: v.member_ledger_uuid, voucher_id: v.voucher_id });
                        setDeskMsg(`Voucher ${v.voucher_id} recognized — ${v.member_label}`);
                        return;
                      }
                    } catch { /* not a voucher */ }
                  }
                  setDeskForm({ ...deskForm, member_uuid: raw, voucher_id: "" });
                }}
                className="w-full rounded-lg border border-stone-700 bg-stone-950 px-4 py-3 text-sm text-stone-100 placeholder:text-stone-600 focus:border-amber-500 focus:outline-none"
              />
              {deskForm.voucher_id && (
                <p data-testid="desk-voucher-flag" className="text-[10px] text-amber-400/90">
                  Voucher {deskForm.voucher_id} bound — settles single-use on their countersign.
                </p>
              )}
              <input
                data-testid="custodian-amount-input"
                type="number" step="0.01" min="0.01" required placeholder="Value received (USD)"
                value={deskForm.amount}
                onChange={(e) => setDeskForm({ ...deskForm, amount: e.target.value })}
                className="w-full rounded-lg border border-stone-700 bg-stone-950 px-4 py-3 text-sm text-stone-100 placeholder:text-stone-600 focus:border-amber-500 focus:outline-none"
              />
              <div className="grid grid-cols-3 gap-2">
                <input
                  data-testid="custodian-fx-currency-input"
                  type="text" maxLength={3} placeholder="GBP"
                  value={deskFx.currency}
                  onChange={(e) => setDeskFx({ ...deskFx, currency: e.target.value.toUpperCase() })}
                  className="rounded-lg border border-stone-700 bg-stone-950 px-3 py-2.5 text-sm text-stone-100 placeholder:text-stone-600 focus:border-amber-500 focus:outline-none"
                />
                <input
                  data-testid="custodian-fx-amount-input"
                  type="number" step="0.01" min="0" placeholder="Local amt"
                  value={deskFx.amount}
                  onChange={(e) => {
                    const next = { ...deskFx, amount: e.target.value };
                    setDeskFx(next);
                    const usd = parseFloat(next.amount) * parseFloat(next.rate);
                    if (usd > 0) setDeskForm({ ...deskForm, amount: usd.toFixed(2) });
                  }}
                  className="rounded-lg border border-stone-700 bg-stone-950 px-3 py-2.5 text-sm text-stone-100 placeholder:text-stone-600 focus:border-amber-500 focus:outline-none"
                />
                <input
                  data-testid="custodian-fx-rate-input"
                  type="number" step="0.0001" min="0" placeholder="Rate→USD"
                  value={deskFx.rate}
                  onChange={(e) => {
                    const next = { ...deskFx, rate: e.target.value };
                    setDeskFx(next);
                    const usd = parseFloat(next.amount) * parseFloat(next.rate);
                    if (usd > 0) setDeskForm({ ...deskForm, amount: usd.toFixed(2) });
                  }}
                  className="rounded-lg border border-stone-700 bg-stone-950 px-3 py-2.5 text-sm text-stone-100 placeholder:text-stone-600 focus:border-amber-500 focus:outline-none"
                />
              </div>
              <p className="text-[10px] text-stone-600">Foreign notes? Enter currency, amount, and your rate — the USD mint fills itself.</p>
              <input
                data-testid="custodian-note-input"
                type="text" placeholder="Note (optional)"
                value={deskForm.note}
                onChange={(e) => setDeskForm({ ...deskForm, note: e.target.value })}
                className="w-full rounded-lg border border-stone-700 bg-stone-950 px-4 py-3 text-sm text-stone-100 placeholder:text-stone-600 focus:border-amber-500 focus:outline-none"
              />
              <button
                data-testid="custodian-attest-button"
                type="submit" disabled={deskBusy}
                className="w-full rounded-lg bg-amber-500 py-3 text-sm font-bold uppercase tracking-widest text-stone-950 hover:bg-amber-400 disabled:opacity-50"
              >
                {deskBusy ? "Logging…" : "Log cash received"}
              </button>
              {deskMsg && <p data-testid="custodian-desk-message" className="text-xs text-amber-300/90 text-center">{deskMsg}</p>}
            </form>
            {desk.held_points.some((p) => p.point_type === "VAULT") && (
              <div className="rounded-xl border border-amber-500/40 bg-stone-900/60 p-4 space-y-3">
                <button
                  data-testid="wire-attest-toggle"
                  onClick={() => { setWireAttest({ ...wireAttest, open: !wireAttest.open }); setAttestMsg(""); }}
                  className="w-full text-left"
                >
                  <div className="text-[10px] uppercase tracking-widest text-amber-400">Director Treasury · Wire attestation</div>
                  <p className="text-[11px] text-stone-400 mt-1">
                    A bank wire landed in the settlement account? Attest it here — the canonical
                    BMEX_NOSTRO_SETTLEMENT_V1 payload is built on-device, signed with your Face ID
                    {me?.passkey_enrolled ? "" : " once enrolled"}, and minted to the member 1:1.
                  </p>
                </button>
                {wireAttest.open && (
                  <form data-testid="wire-attest-form" onSubmit={submitWireAttest} className="space-y-3">
                    <input
                      data-testid="attest-target-input"
                      type="text" required placeholder="Recipient ledger UUID (from their wire memo)"
                      value={wireAttest.target}
                      onChange={(e) => setWireAttest({ ...wireAttest, target: e.target.value })}
                      className="w-full rounded-lg border border-stone-700 bg-stone-950 px-4 py-3 text-sm text-stone-100 placeholder:text-stone-600 focus:border-amber-500 focus:outline-none"
                    />
                    <input
                      data-testid="attest-amount-input"
                      type="number" step="0.01" min="0.01" required placeholder="Wire amount received (USD)"
                      value={wireAttest.amount}
                      onChange={(e) => setWireAttest({ ...wireAttest, amount: e.target.value })}
                      className="w-full rounded-lg border border-stone-700 bg-stone-950 px-4 py-3 text-sm text-stone-100 placeholder:text-stone-600 focus:border-amber-500 focus:outline-none"
                    />
                    <input
                      data-testid="attest-bank-input"
                      type="text" required placeholder="Origin bank (e.g. JPMorgan Chase Bank, N.A.)"
                      value={wireAttest.bank}
                      onChange={(e) => setWireAttest({ ...wireAttest, bank: e.target.value })}
                      className="w-full rounded-lg border border-stone-700 bg-stone-950 px-4 py-3 text-sm text-stone-100 placeholder:text-stone-600 focus:border-amber-500 focus:outline-none"
                    />
                    <input
                      data-testid="attest-ref-input"
                      type="text" required placeholder="Bank reference / Fedwire ID (single-use)"
                      value={wireAttest.ref}
                      onChange={(e) => setWireAttest({ ...wireAttest, ref: e.target.value })}
                      className="w-full rounded-lg border border-stone-700 bg-stone-950 px-4 py-3 text-sm text-stone-100 placeholder:text-stone-600 focus:border-amber-500 focus:outline-none"
                    />
                    <button
                      data-testid="attest-submit-button"
                      type="submit" disabled={attestBusy}
                      className="w-full rounded-lg bg-amber-500 py-3 text-sm font-bold uppercase tracking-widest text-stone-950 hover:bg-amber-400 disabled:opacity-50"
                    >
                      {attestBusy ? (me?.passkey_enrolled ? "Waiting for Face ID…" : "Settling…") : "Attest & mint 1:1"}
                    </button>
                    {attestMsg && <p data-testid="attest-message" className="text-xs text-amber-300/90 text-center leading-relaxed">{attestMsg}</p>}
                  </form>
                )}
              </div>
            )}
            {desk.recent_receipts?.slice(0, 5).map((r) => (
              <div
                key={r.receipt_id}
                data-testid={`desk-receipt-${r.receipt_id}`}
                className="flex items-center justify-between rounded-lg border border-stone-800 bg-stone-900/40 px-4 py-2.5"
              >
                <div>
                  <div className="text-xs text-stone-300">{r.member_label} · {r.direction === "CASH_IN" ? "cash in" : "cash out"}</div>
                  <div className="text-[10px] text-stone-600 font-mono">{r.receipt_id} · {r.status.replaceAll("_", " ")}</div>
                </div>
                <span className="text-sm font-bold text-stone-300">{fmtUsd(r.amount_usd)}</span>
              </div>
            ))}
          </div>
        )}

        {/* activity */}
        <div data-testid="wallet-activity" className="space-y-2">
          <h3 className="text-[10px] uppercase tracking-widest text-stone-500">Recent activity</h3>
          {me?.activity?.length > 0 ? (
            me.activity.map((tx, i) => {
              const incoming = tx.to_address === me.address;
              return (
                <div
                  key={`${tx.settlement_tx?.tx || "tx"}-${i}`}
                  data-testid={`activity-row-${i}`}
                  className="flex items-center justify-between rounded-lg border border-stone-800 bg-stone-900/40 px-4 py-3"
                >
                  <div>
                    <div className="text-xs text-stone-300">{tx.memo || (incoming ? "Received" : "Sent")}</div>
                    <div className="text-[10px] text-stone-600">
                      {new Date(tx.timestamp).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                    </div>
                  </div>
                  <span className={`text-sm font-bold ${incoming ? "text-emerald-400" : "text-stone-300"}`}>
                    {incoming ? "+" : "−"}{fmtUsd(tx.amount_usd)}
                  </span>
                </div>
              );
            })
          ) : (
            <p className="text-xs text-stone-600">No activity yet — your settlements will appear here.</p>
          )}
        </div>
      </div>
    </div>
  );
}
